import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink } from 'lucide-react';
import { projects } from '../../mock';

const BoldProjectsSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  return (
    <section id="projects" className="py-32 px-6 relative bg-[#1A1430]/30">
      <div className="section-watermark" style={{ top: '5%', right: '-5%' }}>04</div>
      
      <div className="max-w-7xl mx-auto" ref={ref}>
        <motion.h2
          className="text-center mb-24 gradient-text"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}
        >
          Selected work
        </motion.h2>
        
        {/* Alternating Left/Right Layout */}
        <div className="space-y-32">
          {projects.map((project, index) => {
            const isEven = index % 2 === 0;
            
            return (
              <motion.div
                key={project.id}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  isEven ? '' : 'lg:grid-flow-dense'
                } ${project.featured ? 'relative' : ''}`}
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                {/* Featured Project Gradient Border Glow */}
                {project.featured && (
                  <div 
                    className="absolute -inset-4 bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#FF5C39] rounded-3xl opacity-30 blur-xl -z-10"
                    style={{
                      animation: 'pulse 3s ease-in-out infinite'
                    }}
                  />
                )}
                
                {/* Image Side */}
                <motion.div
                  className={`${isEven ? '' : 'lg:col-start-2'} relative group`}
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className={`glass-card overflow-hidden relative ${
                    project.featured 
                      ? 'border-2 border-transparent bg-gradient-to-r from-[#7C3AED]/20 via-[#C026D3]/20 to-[#FF5C39]/20 bg-origin-border' 
                      : ''
                  }`}
                  style={project.featured ? {
                    boxShadow: '0 0 30px rgba(124, 58, 237, 0.3), 0 0 60px rgba(192, 38, 211, 0.2)'
                  } : {}}>
                    <motion.img
                      src={project.image}
                      alt={project.name}
                      loading="lazy"
                      className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    
                    {/* Category Badge */}
                    <div className="absolute top-6 left-6">
                      <span className="px-5 py-2 bg-[#0E0B1F]/90 backdrop-blur-sm text-[#FF5C39] rounded-full text-sm font-bold border border-[#FF5C39]/50">
                        {project.category}
                      </span>
                    </div>
                    
                    {/* "Latest" Badge for Featured Project */}
                    {project.isNew && (
                      <div className="absolute top-6 right-6">
                        <span 
                          className="px-4 py-2 bg-gradient-to-r from-[#7C3AED] to-[#FF5C39] text-white rounded-full text-xs font-bold uppercase tracking-wider shadow-lg"
                          style={{
                            boxShadow: '0 4px 15px rgba(124, 58, 237, 0.5)'
                          }}
                        >
                          Latest
                        </span>
                      </div>
                    )}
                    
                    {/* Gradient Overlay on Hover */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-t from-[#0E0B1F] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    />
                  </div>
                </motion.div>
                
                {/* Content Side */}
                <motion.div
                  className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}
                  initial={{ opacity: 0, x: isEven ? 60 : -60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                >
                  <h3 className="text-4xl md:text-5xl font-bold text-[#EDE9FE] mb-8" style={{ fontFamily: 'Clash Display, Inter, sans-serif' }}>
                    {project.name}
                  </h3>
                  
                  {/* Challenge / Approach / Outcome */}
                  <div className="space-y-6 mb-8">
                    <div>
                      <span className="text-sm text-[#7C3AED] font-bold uppercase tracking-wider">Challenge</span>
                      <p className="text-xl text-[#EDE9FE] mt-2">{project.challenge}</p>
                    </div>
                    
                    <div>
                      <span className="text-sm text-[#C026D3] font-bold uppercase tracking-wider">Approach</span>
                      <p className="text-xl text-[#EDE9FE] mt-2">{project.approach}</p>
                    </div>
                    
                    <motion.div
                      whileHover={{ x: 5 }}
                    >
                      <span className="text-sm text-[#22D3A5] font-bold uppercase tracking-wider">Outcome</span>
                      <p className="text-xl text-[#22D3A5] font-bold mt-2">
                        {project.outcome}
                      </p>
                    </motion.div>
                  </div>
                  
                  {/* Visit Link with Magnetic Effect */}
                  <motion.a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#7C3AED] to-[#C026D3] text-white rounded-full font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:shadow-[#7C3AED]/50"
                    whileHover={{ scale: 1.05, x: 5 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Visit project
                    <ExternalLink size={20} />
                  </motion.a>
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BoldProjectsSection;